import { useContext } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  FormHelperText,
  FormControl,
  Grid,
  MenuItem,
  IconButton,
  InputLabel,
  TextField,
  Select,
  Stack
} from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

import * as FirestoreDataService from '../services/FirestoreDataService';
import { AuthContext } from '../auth/auth-context';
import { setSelectedDevice, showAppAlert } from 'store/reducers/appreducer';
import { addDevice } from 'services/apiService';
import useToken from 'services/useToken';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}));

const DeviceDialog = ({ open, handleClose, isEditMode, values }) => {
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const {token, setToken} = useToken();

  const setDevice = (deviceId) => {
    dispatch(setSelectedDevice({ selectedDevice: deviceId }));
  };

  const INITIAL_VALUES = {
    name: '',
    IMEI: '',
    model: '',
    submit: null
  };
  const msgPrefix = isEditMode ? 'Updated' : 'Added';

  return (
    <BootstrapDialog fullWidth={true} maxWidth="xs" onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
      <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        {!isEditMode ? 'Add Device' : 'Edit Device'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>
      <Formik
        initialValues={isEditMode ? values : INITIAL_VALUES}
        enableReinitialize={true}
        validationSchema={Yup.object().shape({
          name: Yup.string().max(255).required('Device name is required'),
          IMEI: Yup.string().matches(/^\d+$/, 'Device IMEI must be a number').max(255).required('Device IMEI is required'),
          model: Yup.string().max(255).required('Device Model is required')
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            setStatus({ success: false });
            setSubmitting(true);
            // console.log('values', values);

            const data = {
              IMEI: values.IMEI,
              model: values.model,
              device_name: values.name,
            }

            await addDevice(data, token)
              .then((response) => {
                // console.log('Response', response);
                if(!response.error) {
                  setDevice(response.data.IMEI);
                  handleClose();
                  dispatch(showAppAlert({ show: true, alertType: 'success', alertMessage: 'Added device successfully!' }));
                }
                else {
                  dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: `${response.errorMessage}`}))
                }
                
              })
              .catch((error) => {
                dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: 'Failed to add device!' }));
                console.info('Failed to add device!', error);
              });
            /* 
            await FirestoreDataService.getUserDocument(user)
              .then(async (documentSnap) => {
                if (documentSnap.exists()) {
                  let devices = documentSnap.data().devices;
                  const newDevice = {
                    id: values.IMEI,
                    IMEI: values.IMEI,
                    name: values.name,
                    model: values.model
                  };
                  if (isEditMode) {
                    devices = devices.map((device) => {
                      if (device.id === values.id) {
                        //replace device with updated values
                        return values;
                      }
                      return device;
                    });
                  } else {
                    devices.push(newDevice);
                  }
                  await FirestoreDataService.updateUserDocument(user, { devices: devices })
                    .then(() => {
                      handleClose();
                      console.info(msgPrefix + ' device data successfully!');
                      dispatch(showAppAlert({ show: true, alertType: 'success', alertMessage: msgPrefix + ' device data successfully!' }));
                    })
                    .catch((e) => {
                      console.info(msgPrefix + ' device data failed!', e);
                      dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msgPrefix + ' device data failed!' }));
                    });
                }
              })
              .catch((e) => {
                dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: 'Failed to add/update device!' }));
                console.info('Failed to add/update device!', e);
              });
            */
            setSubmitting(false);
          } catch (err) {
            setStatus({ success: false });
            setErrors({ submit: err.message });
            setSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <TextField
                      fullWidth
                      id="device-name"
                      type="text"
                      value={values.name}
                      name="name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Enter device name"
                      error={Boolean(touched.name && errors.name)}
                      label="Device Name"
                      helpertext={touched.name && errors.name}
                    />
                  </Stack>
                  {touched.name && errors.name && (
                    <Grid item xs={12}>
                      <FormHelperText error>{errors.name}</FormHelperText>
                    </Grid>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <TextField
                      fullWidth
                      id="device-IMEI"
                      type="text"
                      value={values.IMEI}
                      name="IMEI"
                      disabled={isEditMode}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Enter device IMEI"
                      error={Boolean(touched.IMEI && errors.IMEI)}
                      label="Device IMEI"
                      helpertext={touched.IMEI && errors.IMEI}
                      inputProps={{
                        pattern: '\\d*',
                        inputMode: 'numeric'
                      }}
                    />
                  </Stack>
                  {touched.IMEI && errors.IMEI && (
                    <Grid item xs={12}>
                      <FormHelperText error>{errors.IMEI}</FormHelperText>
                    </Grid>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <FormControl disabled={isEditMode}>
                      <InputLabel id="demo-simple-select-autowidth-label">Select Model</InputLabel>
                      <Select
                        labelId="demo-simple-select-autowidth-label"
                        label="Select Model"
                        id="device-Model"
                        value={values.model}
                        name="model"
                        placeholder="Select device model"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(touched.model && errors.model)}
                        helpertext={touched.model && errors.model}
                      >
                        <MenuItem value="AW04">AW04</MenuItem>
                        <MenuItem value="AW05">AW05</MenuItem>
                        <MenuItem value="AW07">AW07</MenuItem>
                        <MenuItem value="AW10">AW10</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  {touched.model && errors.model && (
                    <Grid item xs={12}>
                      <FormHelperText error>{errors.model}</FormHelperText>
                    </Grid>
                  )}
                </Grid>
                {errors.submit && (
                  <Grid item xs={12}>
                    <FormHelperText error>{errors.submit}</FormHelperText>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button disableElevation disabled={isSubmitting} onClick={handleClose} variant="outlined">
                Cancel
              </Button>
              <Button disableElevation disabled={isSubmitting} type="submit" variant="contained" color="primary">
                Save
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </BootstrapDialog>
  );
};

DeviceDialog.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  isEditMode: PropTypes.bool,
  values: PropTypes.object
};

export default DeviceDialog;
